import { MAX_REGRESSION_RECENT_WEEKS, WEEK_MS } from '@heraion/shared';

import { asyncStorageResourceCache } from './state/asyncListCache';

// Offline fallback for the logger's "% of best" (LG-20).
//
// `useEntryHistory`'s recent-window cache is module-scope and in-memory
// only: it survives the Logger modal closing and reopening, but not the
// process. Its filler is a GET that degrades to `[]` on error, so on a
// cold launch with no network `getBestE1rmForSlug` returns null and the
// percentage simply never renders — the athlete gets no comparison at
// all rather than a slightly old one.
//
// This module keeps the LAST KNOWN best-e1RM per exercise slug on disk so
// that path has something to fall back on. What it stores is whatever
// `getBestE1rmForSlug` itself returned while online (see `bestE1rmBySlug`
// in entryHistoryE1rm.ts) — the map is *defined* as the live function's own
// output, so the offline number can't drift from the online one by
// re-deriving it with different rules.
//
// Persistence goes through the shared `asyncStorageResourceCache`
// adapter, which already owns the `jl.asyncList.<key>.v<n>` namespace,
// the safe-parse-or-null read and the best-effort write.

type Snapshot = {
  /** When the snapshot was taken, ISO. Its own field rather than the
   *  adapter's internal `cachedAt`, which `read()` does not surface. */
  savedAt: string;
  bestBySlug: Record<string, number>;
};

const cache = asyncStorageResourceCache<Snapshot>('logger/bestE1rmBySlug');

/** How long a snapshot may sit on disk and still be shown as current
 *  capacity. Past it we show nothing rather than something stale: an
 *  absent comparison must look absent, not wrong.
 *
 *  ⚠️ The number is BORROWED, not shared. `MAX_REGRESSION_RECENT_WEEKS`
 *  (`packages/shared/src/maxRegression.ts:47`) answers a different
 *  question — how far back to *search* for a corroborating best e1RM —
 *  and this gate answers how long a persisted copy stays trustworthy
 *  while the app is offline. They are the same duration because "4 weeks
 *  ≈ a mesocycle" is the right horizon for both, not because one governs
 *  the other; borrowing it avoids a second independent 4 drifting from
 *  the first. Whoever changes the corroboration window for MAX-regression
 *  reasons should decide deliberately whether this follows. */
const BEST_E1RM_FALLBACK_MAX_AGE_MS = MAX_REGRESSION_RECENT_WEEKS * WEEK_MS;

// Hydrated copy, module-scope so the sync read path can consult it.
// Null = not hydrated, or hydrated to nothing usable.
let fallbackBySlug: Record<string, number> | null = null;

/** Load the persisted snapshot into memory, dropping it if it is older
 *  than the window above or unreadable. Safe to call repeatedly; the
 *  Provider mounts on every Logger open, not once per launch. */
export async function hydrateBestE1rmFallback(nowMs: number): Promise<void> {
  const snap = await cache.read();
  if (snap === null) {
    fallbackBySlug = null;
    return;
  }
  const savedMs = new Date(snap.savedAt).getTime();
  // A missing/garbled savedAt is treated as unusable rather than as
  // "age 0": an unbounded stale best is the outcome this gate exists to
  // prevent, so an unreadable stamp must fail closed.
  if (!Number.isFinite(savedMs) || nowMs - savedMs > BEST_E1RM_FALLBACK_MAX_AGE_MS) {
    fallbackBySlug = null;
    return;
  }
  const map = snap.bestBySlug;
  fallbackBySlug =
    map !== null && typeof map === 'object' && !Array.isArray(map) ? map : null;
}

/** The last known best e1RM (kg) for a slug, or null. Only consulted by
 *  `getBestE1rmForSlug` when the live window is cold — online behaviour
 *  is unchanged. */
export function getFallbackBestE1rm(exerciseId: string): number | null {
  if (fallbackBySlug === null) return null;
  const v = fallbackBySlug[exerciseId];
  // A non-finite or non-positive value is the same "no qualifying
  // history" case the live path reports as null, not a zero to render.
  // The `typeof` arm is REDUNDANT and deliberately kept: `Number.isFinite`
  // (unlike the global `isFinite`) does not coerce, so it already rejects
  // `'100'` on its own — dropping the arm is an equivalent mutation, and
  // it was verified to be one rather than assumed. It stays because the
  // declared type says `number` while the value came out of JSON.parse.
  return typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null;
}

/** Retire the snapshot: the server answered and has no recent sessions
 *  for this athlete. Distinct from a failed fetch, which must leave the
 *  snapshot alone — an empty answer and no answer are not the same
 *  thing, and only the first one is evidence. */
export async function clearBestE1rmSnapshot(nowMs: number): Promise<void> {
  fallbackBySlug = null;
  // An honest empty record rather than a removed row: the adapter has no
  // delete, and "as of now, nothing" is exactly what was learned.
  await cache.write({ savedAt: new Date(nowMs).toISOString(), bestBySlug: {} });
}

/** Write a fresh snapshot. Best-effort: the adapter swallows storage
 *  failures, and losing this cache only costs the offline percentage. */
export async function persistBestE1rmSnapshot(
  bestBySlug: Record<string, number>,
  nowMs: number,
): Promise<void> {
  // Nothing to say is not the same as "the athlete has no history": an
  // empty map is exactly what a failed fetch produces, and writing it
  // would erase a good snapshot the next time the app opens offline.
  if (Object.keys(bestBySlug).length === 0) return;
  await cache.write({
    savedAt: new Date(nowMs).toISOString(),
    bestBySlug,
  });
  fallbackBySlug = bestBySlug;
}
